import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ZoomIn, X } from 'lucide-react';
import Button from '@ajat/design-system/Button';
import GalleryBack from './GalleryBack';

/* The left column of a product/package page when the variation's layout is a
 * 3D template: same `.pp__gallery` slot as the photo Gallery (useProductPage
 * queries that class for scroll restoration), one WebGL instance per page,
 * lazy-loaded so the photo pages never pay for three.js. */
const Product3DPreview = lazy(() => import('@ajat/design-system/Product3DPreview'));

/* WebGL availability, probed once. Devices without it (blocked GPU, old
 * browsers, some in-app webviews) get the photo gallery instead of a crash. */
let webglProbe = null;
export const threeDSupported = () => {
  if (webglProbe != null) return webglProbe;
  try {
    const c = document.createElement('canvas');
    webglProbe = !!(c.getContext('webgl2') || c.getContext('webgl') || c.getContext('experimental-webgl'));
  } catch { webglProbe = false; }
  return webglProbe;
};

/* A failing WebGL context (or any render error inside three.js) must not take
 * the product page down: report it and let the page fall back to photos. */
class ThreeDErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error) { this.props.onError?.(error); }
  render() { return this.state.failed ? null : this.props.children; }
}
ThreeDErrorBoundary.propTypes = { onError: PropTypes.func, children: PropTypes.node };
ThreeDErrorBoundary.defaultProps = { onError: undefined, children: null };

const ThreeDGallery = ({ threeD, frontLabel, backLabel, onError, focusView, focusTick, zoomed, onZoomChange }) => {
  const [view, setView] = useState('front');
  // The page asks for a camera preset when the customer touches a mapped
  // attribute (focusTick bumps so the same preset can be re-requested).
  useEffect(() => { if (focusView) setView(focusView); }, [focusView, focusTick]);
  const [flagImage, setFlagImage] = useState(null);
  const rootRef = useRef(null);

  // Desktop: the column is sticky at top 0 but starts below the page header,
  // so its viewport-based height would run under the sticky footer until the
  // header has scrolled away. Publish the live offset from the viewport top as
  // --pp-gallery-top; Gallery.css subtracts it (and the footer) from 100vh.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof window === 'undefined') return undefined;
    let raf = 0;
    let last = -1;
    const update = () => {
      raf = 0;
      const top = Math.max(0, Math.round(el.getBoundingClientRect().top));
      if (top !== last) { last = top; el.style.setProperty('--pp-gallery-top', `${top}px`); }
    };
    const schedule = () => { if (!raf) raf = window.requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);
  const flag = threeD?.props?.flag || null;

  // Flags are composed from the shipped SVG/WebP artwork (never uploaded):
  // "tryk:<iso>" pairs the country with DK (composeTrykFlagPair → canvas),
  // "broderi:<preset>" is a patch whose green backdrop is keyed out
  // (keyOutGreenBackdrop → canvas, or null = use the file as is). Canvases are
  // drawn directly by the overlay painter; any failure just leaves the slot empty.
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    if (!flag) { setFlagImage(null); return undefined; }
    // Dynamic import: flagArtwork pulls three.js via modelLoader, which must
    // stay out of the main bundle (the preview itself is lazy-loaded).
    import('@ajat/design-system/Product3DPreview/flagArtwork')
      .then((m) => (flag.kind === 'broderi'
        ? m.keyOutGreenBackdrop(m.broderiFlagUrl(flag.code)).then((blob) => blob || m.broderiFlagUrl(flag.code))
        : m.composeTrykFlagPair(flag.code)))
      .then((result) => {
        if (cancelled) return;
        if (typeof result === 'string') { setFlagImage(result); return; }
        // Keyed-out patch / composed pair come back as a canvas: drawable directly, no encode/decode.
        if (result && ((typeof HTMLCanvasElement !== 'undefined' && result instanceof HTMLCanvasElement)
          || (typeof ImageBitmap !== 'undefined' && result instanceof ImageBitmap))) { setFlagImage(result); return; }
        if (result && typeof Blob !== 'undefined' && result instanceof Blob) {
          objectUrl = URL.createObjectURL(result);
          setFlagImage(objectUrl);
          return;
        }
        setFlagImage(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setFlagImage(null);
        console.warn('[ThreeDGallery] flag artwork failed', flag, err); // eslint-disable-line no-console
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [flag?.kind, flag?.code]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile fullscreen: the page's zoom mode (`.pp--zoomed`) hides header,
  // configurator and footer and stretches this column to 100dvh; we also lock
  // the document scroll so nothing underneath moves or receives taps.
  const scrollYBeforeZoom = useRef(0);
  const openFullscreen = useCallback(() => {
    scrollYBeforeZoom.current = window.scrollY;
    onZoomChange?.(true);
    window.scrollTo(0, 0);
  }, [onZoomChange]);
  const closeFullscreen = useCallback(() => {
    onZoomChange?.(false);
    window.requestAnimationFrame(() => window.scrollTo(0, scrollYBeforeZoom.current));
  }, [onZoomChange]);
  useEffect(() => {
    if (!zoomed || typeof document === 'undefined') return undefined;
    const { body, documentElement: html } = document;
    const prev = { body: body.style.overflow, html: html.style.overflow, touch: body.style.touchAction };
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    const onKey = (e) => { if (e.key === 'Escape') closeFullscreen(); };
    window.addEventListener('keydown', onKey);
    return () => {
      body.style.overflow = prev.body;
      html.style.overflow = prev.html;
      body.style.touchAction = prev.touch;
      window.removeEventListener('keydown', onKey);
    };
  }, [zoomed, closeFullscreen]);

  const { flag: _f, ...rest } = threeD?.props || {};
  const props = useMemo(() => rest, [threeD]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!threeD) return null;

  return (
    <div
      ref={rootRef}
      className="pp__gallery pp__gallery--3d"
      data-three-d={JSON.stringify({ template: threeD.template, ...rest, flag: flag || undefined })}
    >
      <div className="pp__gallery-3d-slot">
        <ThreeDErrorBoundary onError={onError}>
        <Suspense fallback={<div className="pp__gallery-3d-loading" />}>
          <Product3DPreview
            modelUrl={threeD.modelUrl}
            modelConfig={threeD.modelConfig}
            {...props}
            flagImage={flagImage}
            view={view}
            onViewChange={setView}
            frontLabel={frontLabel}
            backLabel={backLabel}
          />
        </Suspense>
        </ThreeDErrorBoundary>
        {onZoomChange && !zoomed && (
          <div className="pp__zoom-btn-container pp__gallery-3d-zoom">
            <Button size="tertiary" variant="white" iconOnly iconLeft={<ZoomIn size={20} />} onClick={openFullscreen} aria-label="Fuld skærm" />
          </div>
        )}
        {/* Same gray top-right close as the photo gallery's zoom (.pp__zoom-close-container). */}
        {onZoomChange && zoomed && (
          <div className="pp__zoom-close-container">
            <Button size="tertiary" variant="gray" iconOnly iconLeft={<X size={20} />} onClick={closeFullscreen} aria-label="Luk fuld skærm" />
          </div>
        )}
      </div>
      {/* After the slot so it paints over the canvas; placement in Gallery.css. */}
      <GalleryBack />
    </div>
  );
};

ThreeDGallery.propTypes = {
  threeD: PropTypes.shape({ template: PropTypes.string, modelUrl: PropTypes.string, modelConfig: PropTypes.object, props: PropTypes.object }),
  frontLabel: PropTypes.string,
  backLabel: PropTypes.string,
  onError: PropTypes.func,
  focusView: PropTypes.string,
  focusTick: PropTypes.number,
  /** Mobile fullscreen (the page's zoom mode); the buttons render only when onZoomChange is given. */
  zoomed: PropTypes.bool,
  onZoomChange: PropTypes.func,
};
ThreeDGallery.defaultProps = { threeD: null, frontLabel: 'Forsiden', backLabel: 'Bagsiden', onError: undefined, focusView: null, focusTick: 0, zoomed: false, onZoomChange: undefined };

export default ThreeDGallery;
